//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { AccessGuids, ConfigSystemParams, Duration, StorageValues } from 'src/app/core/consts';
import { AuthenticationService } from '../../auth.service';
import { catchError, throwError } from 'rxjs';
import { ComponentAccessUserService } from '../system/component-access/component-access-user-service';
import { ConfigSystemService } from '../system/config-system/config-system-service';
import { DecryptAesClientSide } from 'src/app/core/encryption';
import { ExceptionsService } from './exceptions-service';
import { Injectable, NgZone } from '@angular/core';
import { ModalForceCloseService } from './modal-force-close-service';
import { Router } from '@angular/router';
import { StorageService } from './storage-service';

@Injectable({ providedIn: 'root' })

export class AutoLogoutService {

  get autoLogoutEnabled   () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.AutoLogoutEnabled,              false)&&
                                      !this.componentAccessUserService.hasAccessGuid(AccessGuids.LoginOrLogoutAutoLogoutGuard,      true ); }
  get divertAllToLoginForm() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.AutoLogoutDivertAllToLoginForm, true ); }

  get autoLogoutMillisecs() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.AutoLogoutMilliSecs, Duration._5MinMilli ); }

  private handlerActivity=() => this.reset();

  private readonly activityEvents: string[]=['click', 'keydown', 'scroll', 'touchstart', 'wheel'];

  private lastActivityUtc: number=0;
  private listenersActive: boolean=false;
  private timeoutId      : any;

  constructor(
    private authenticationService     : AuthenticationService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private exceptionsService         : ExceptionsService,
    private modalForceCloseService    : ModalForceCloseService,
    private ngZone                    : NgZone,
    private router                    : Router,
    private storageService            : StorageService
  ) {

  }

  private clearTimer(): void {
    try {
      if (this.timeoutId != undefined) {
        clearTimeout(this.timeoutId);
        this.timeoutId=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private endServerSession(): void {
    try {
      this.authenticationService.logoutApi()
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
        })
      )
      .subscribe({
        next : () => {
          this.finaliseLogout();
        },
        error: error => {
          this.finaliseLogout();
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.finaliseLogout();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private finaliseLogout(): void {
    try {
      this.authenticationService.logout();
      this.router.navigate(['/auth/login']);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private initListener(): void {
    try {
      if (this.listenersActive)
        return;

      this.activityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.handlerActivity, { passive: true });
      });

      this.listenersActive=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private isUserLoggedIn(): boolean {
    try {
      return !!this.storageService.getItem(StorageValues.encCurrentUser);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private logout(): void {
    try {
      if (!this.autoLogoutEnabled)
        return;

      this.clearTimer();

      if (!this.isUserLoggedIn())
        return;

      this.modalForceCloseService.dismissAllForced();

      const SsoLoggedIn: boolean=((DecryptAesClientSide(this.storageService.getItem(StorageValues.encSsoLoggedIn)) ?? "").toUpperCase()=="TRUE");

      if (this.divertAllToLoginForm || SsoLoggedIn)
        this.endServerSession();
      else
        this.router.navigate(['/auth/lockscreen/basic']);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private reset(): void {
    try {
      this.initListener();

      this.lastActivityUtc=new Date().getTime();

      this.clearTimer();

      if (!this.autoLogoutEnabled
          || this.autoLogoutMillisecs<=0
          || !this.isUserLoggedIn()) {
        return;
      }

      this.ngZone.runOutsideAngular(() => {
        this.timeoutId=setTimeout(() => this.timerElapsed(), this.autoLogoutMillisecs);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private timerElapsed(): void {
    try {
      if (!this.isUserLoggedIn()) {
        this.clearTimer();
        return;
      }

      const idleMillisecs=new Date().getTime()-this.lastActivityUtc;
      const remaining    =this.autoLogoutMillisecs-idleMillisecs;

      if (remaining>0) {
        this.ngZone.runOutsideAngular(() => {
          this.timeoutId=setTimeout(() => this.timerElapsed(), remaining);
        });

        return;
      }

      this.ngZone.run(() => this.logout());
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public start(): void {
    try {
      this.reset();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public stop(): void {
    try {
      this.clearTimer();

      if (!this.listenersActive)
        return;

      this.activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, this.handlerActivity);
      });

      this.listenersActive=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
